import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Assurez-vous d'utiliser 'react-router-dom' pour Link et useLocation
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import usePermissions from "../../hooks/usePermissions";
import {
  // ✅ Importation corrigée : Utilisation de Lock au lieu de LockClosed
  LayoutDashboard,
  Package,
  Settings,
  ShoppingCart,
  ShoppingBag,
  Heart,
  Wallet,
  ChevronDown,
  Lock,
  User,
  MessageSquare,
} from "lucide-react";

// --- COMPOSANT POUR L'ICÔNE PERSONNALISÉE : Gestion des accès / roles ---
/**
 * Composant d'icône combinant un cadenas et un profil utilisateur
 */
const AccessRoleIcon = ({ className }) => (
  <div className={`relative ${className}`}>
    {/* Utilisation de l'icône Lock (cadenas fermé) */}
    <Lock className="w-5 h-5" />
    {/* Petit rond avec icône de profil en bas à droite */}
    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-white rounded-full flex items-center justify-center border border-gray-300">
        <User className="w-2 h-2 text-gray-700" />
    </div>
  </div>
);

// --- TABLEAU DE NAVIGATION (navItems) ---
// Chaque item porte la permission requise pour être visible dans la sidebar.
const navItems = [
  {
    icon: <LayoutDashboard />,
    name: "Tableau de bord",
    path: "/dashboard",
    permission: "dashboard:read",
  },
  {
    icon: <ShoppingCart />,
    name: "Commandes",
    subItems: [
      {
        name: "Commandes",
        path: "/dashboard/orders",
        permission: "orders:read",
      },
      {
        name: "Codes promo",
        path: "/dashboard/promos",
        permission: "promos:read",
      },
    ],
  },
  {
    icon: <Package />,
    name: "Produits",
    path: "/dashboard/products",
    permission: "products:read",
  },
  {
    icon: <MessageSquare />,
    name: "Avis clients",
    path: "/dashboard/reviews",
    permission: "reviews:read",
  },
  // ✨ "Mes commandes" : accessible à TOUS les sellers (pas de `permission`).
  // Permet à chacun de voir les commandes qu'il a passées en tant que client.
  {
    icon: <ShoppingBag />,
    name: "Mes commandes",
    path: "/dashboard/my-orders",
  },
  // ✨ "Mes favoris" : accessible à TOUS les sellers aussi.
  {
    icon: <Heart />,
    name: "Mes favoris",
    path: "/dashboard/my-favorites",
  },
  {
    icon: <Wallet />,
    name: "Mes revenus",
    path: "/dashboard/revenues",
    permission: "revenues:read",
  },
  // ✅ Gestion des accès / roles
  {
    icon: <AccessRoleIcon className="menu-item-icon-size" />,
    name: "Gestion des accès / roles",
    path: "/dashboard/access-roles",
    permission: "access-roles:read",
  },
  {
    icon: <Settings />,
    name: "Paramètres",
    path: "/dashboard/settings",
    permission: "settings:read",
  },
];

// -------------------------------------------------------------------------
// --- COMPOSANT PRINCIPAL : AppSidebar (Logique inchangée) ---
// -------------------------------------------------------------------------

const AppSidebar = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const { can, isLoading } = usePermissions();

  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [subMenuHeight, setSubMenuHeight] = useState({});
  const subMenuRefs = useRef({});

  // Filtre la navigation selon les permissions de l'utilisateur connecté.
  const visibleNavItems = useMemo(() => {
    return navItems
      .map((nav) => {
        if (nav.subItems) {
          const allowedSubs = nav.subItems.filter(
            (s) => !s.permission || can(s.permission)
          );
          if (allowedSubs.length === 0) return null;
          return { ...nav, subItems: allowedSubs };
        }
        if (nav.permission && !can(nav.permission)) return null;
        return nav;
      })
      .filter(Boolean);
  }, [can]);

  const isActive = useCallback(
    (path) => location.pathname === path,
    [location.pathname]
  );

  useEffect(() => {
    visibleNavItems.forEach((nav, index) => {
      if (nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({
              type: "main",
              index,
            });
          }
        });
      }
    });
  }, [location, isActive, visibleNavItems]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index, menuType) => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items) => (
    <ul className="flex flex-col gap-4">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, "main")}
              className={`inline-flex gap-3 py-2 px-3 w-full rounded-lg transition-colors ${
                openSubmenu?.type === "main" && openSubmenu?.index === index
                  ? "text-[#E5502E] font-semibold bg-orange-50"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              } cursor-pointer w-full ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.type === "main" && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="menu-item-text"> {nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDown 
                  className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                    openSubmenu?.type === "main" &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-brand-500"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`inline-flex gap-3 py-2 px-3 w-full rounded-lg transition-colors ${
                  isActive(nav.path)
                    ? "bg-[#E5502E] text-white hover:text-white hover:bg-[#E5502E]"
                    : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`main-${index}`] = el;
              }}
              className="overflow-hidden w-full"
              style={{
                height:
                  openSubmenu?.type === "main" && openSubmenu?.index === index
                    ? `${subMenuHeight[`main-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-2 ml-6 w-full">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name} className="w-[full mb-4">
                    <Link
                      to={subItem.path}
                      className={`block rounded-lg px-4 py-2 w-full transition-colors ${
                        isActive(subItem.path)
                          ? "bg-[#E5502E] text-white hover:text-white hover:bg-[#E5502E]"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 max-w-full"
                      }`}
                    >
                      {subItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <img
                className="dark:hidden"
                src={process.env.PUBLIC_URL + "/images/logo.png"}
                alt="Logo"
                width={150}
                height={40}
              />
              <img
                className="hidden dark:block"
                src={process.env.PUBLIC_URL + "/images/logo.png"}
                alt="Logo"
                width={150}
                height={40}
              />
            </>
          ) : (
            <img
              src={process.env.PUBLIC_URL + "/images/logo.png"}
              alt="Logo"
              width={32}
              height={32}
            />
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              className="w-full px-4 py-2 pl-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-400">Chargement…</div>
              ) : visibleNavItems.length > 0 ? (
                renderMenuItems(visibleNavItems)
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400">
                  Aucun accès disponible
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;